<template>
  <div
    class="x-tooltip"
    :class="[theme]"
    role="x-tooltip"
    tabindex="0"
    :style="{
      width
    }"
    @blur="onBlur"
  >
    <div
      v-if="title"
      class="title"
    >
      {{ title }}
    </div>
    <slot />

    <div
      ref="arrow"
      class="x-tooltip-arrow"
      :data-popper-arrow="options.placement"
    />
  </div>
</template>

<script>
import { createPopper } from '@popperjs/core'

const showEvents = ['mouseenter', 'focus']
const hideEvents = ['mouseleave', 'blur']

export default {
  model: {
    prop: 'visible',
    event: 'close'
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    options: {
      type: Object,
      default: () => ({})
    },
    title: {
      type: String,
      default: ''
    },
    selector: {
      type: [Object, String, HTMLAnchorElement, HTMLSpanElement],
      default: ''
    },
    width: {
      type: String,
      default: ''
    },
    // 主题色，默认为 light, 支持 dark
    theme: {
      type: String,
      default: 'light'
    },
    // 唤起类型, 默认 focus，支持 click
    type: {
      type: String,
      default: 'focus'
    },
    clickLayoutVisible: {
      type: Boolean,
      default: false
    },
    delay: {
      type: Number,
      default: 0
    },
    tabIndex: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      popTarget: null,
      popperInstance: null,
      showTooltipTimeout: null
    }
  },
  mounted() {
    this.$nextTick(() => {
      const { selector } = this
      if (selector) {
        const target = typeof selector === 'object' ? selector : document.querySelector(selector)
        if (!target) return false

        this.popTarget = target
        const { offset = [0, 10], ...resetOptions } = this.options
        this.popperInstance = createPopper(target, this.$el, {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset
              }
            }
          ],
          ...resetOptions
        })
        this.init()
      }
    })
  },
  beforeDestroy() {
    if (!this.popTarget) return false
    const button = this.popTarget

    showEvents.forEach((event) => {
      button.removeEventListener(event, this.handleShow)
    })

    hideEvents.forEach((event) => {
      button.removeEventListener(event, this.handleHide)
    })
  },
  methods: {
    init() {
      if (this.type === 'alwaysShow') {
        if (this.visible) {
          this.handleShow()
        }

        this.$watch('visible', (value) => {
          if (value) {
            this.handleShow()
          } else {
            this.handleHide()
          }
        })
        return
      }

      if (this.type === 'click') {
        this.$watch('visible', (value) => {
          if (value) {
            this.handleShow()
          } else {
            this.handleHide()
          }
        })
        return
      }

      const button = this.popTarget

      showEvents.forEach((event) => {
        button.addEventListener(event, this.handleShow)
      })

      hideEvents.forEach((event) => {
        button.addEventListener(event, this.handleHide)
      })
    },
    handleShow() {
      if (this.delay != null) {
        this.showTooltipTimeout = setTimeout(() => {
          this.showTooltip()
        }, this.delay)
        return
      }

      this.showTooltip()
    },
    showTooltip() {
      this.$el.setAttribute('data-show', '')
      // Enable the event listeners
      // popperInstance.setOptions({
      //   modifiers: [{ name: 'eventListeners', enabled: true }]
      // })
      // We need to tell Popper to update the tooltip position
      // after we show the tooltip, otherwise it will be incorrect
      this.popperInstance.update()
      if (this.type === 'alwaysShow') return

      this.$nextTick(() => {
        this.$el.focus()
      })
    },
    handleHide() {
      clearTimeout(this.showTooltipTimeout)
      this.$el.removeAttribute('data-show')
      // Disable the event listeners
      // popperInstance.setOptions({
      //   modifiers: [{ name: 'eventListeners', enabled: false }]
      // })
    },
    onBlur() {
      if (!this.clickLayoutVisible && (this.type === 'click' || this.type === 'alwaysShow')) return

      console.log('blur')
      this.handleHide()
      this.$emit('close')
    },
    updatePopperInstance() {
      this.popperInstance?.update()
    }
  }
}
</script>

<style lang="less">
.x-tooltip {
  z-index: 19;
  display: none;
  width: 260px;
  box-sizing: border-box;
  padding: 10px;
  border-radius: 8px;
  box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 30%);
  font-size: 12px;
  outline: none;
  user-select: none;

  &.light {
    background: #f8f8f8;
    color: #333;

    .x-tooltip-arrow::before {
      background: #f8f8f8;
    }
  }

  &.dark {
    background: #333;
    color: #fff;
  }

  .title {
    margin-bottom: 5px;
    font-size: 14px;
    line-height: 16px;
  }

  &[data-show] {
    display: block;
  }
}

.x-tooltip-arrow,
.x-tooltip-arrow::before {
  position: absolute;
  z-index: -1;
  width: 8px;
  height: 8px;
}

.x-tooltip-arrow::before {
  background: #333;
  content: "";
  transform: rotate(45deg);
}

.x-tooltip[data-popper-placement^="top"] > .x-tooltip-arrow {
  bottom: -4px;
}

.x-tooltip[data-popper-placement^="bottom"] > .x-tooltip-arrow {
  top: -4px;
}

.x-tooltip[data-popper-placement^="left"] > .x-tooltip-arrow {
  right: -4px;
}

.x-tooltip[data-popper-placement^="right"] > .x-tooltip-arrow {
  left: -4px;
}
</style>
